import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/ProductFeaturesService';
import { ProductFeature } from '../types';

export const useProductFeature = (productFeatureId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const productFeatureQuery = useQuery({
        queryKey: ['product-feature', productFeatureId],
        queryFn: () => fetch(productFeatureId!),
        enabled: !!productFeatureId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: ProductFeature) => {
            const { created, updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product-features'] });
        }
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: ProductFeature) => {
            if (!productFeatureId) throw new Error('productFeatureId is required parameter');
            const { created, updated, ...rest } = values;
            return update(productFeatureId, rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product-features'] });
            queryClient.invalidateQueries({ queryKey: ['product-feature', productFeatureId] });
        }
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!productFeatureId) throw new Error('productFeatureId is required parameter');
            return deleteRecord(productFeatureId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product-features'] });
        }
    });

    return {
        record: productFeatureQuery.data,
        loading: productFeatureQuery.isLoading,
        fetchRecord: productFeatureQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};